import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { firstAllowedPath } from '../utils/rbacDefaultRoute';
import { hasFullRbacMenuBypass } from '../utils/rbacSecurityAccess';
import { showError, showSuccess } from '../utils/notifications';
import { Eye, EyeOff } from 'lucide-react';
import XTAMSpinner from '../components/XTAMSpinner';
import './LoginPage.css';

const baseUrl = process.env.PUBLIC_URL || '';
const logoXtam = `${baseUrl.replace(/\/$/, '')}/img/${encodeURIComponent('LOGO XTAM.png')}`;

const REMEMBER_EMAIL_KEY = 'vms_remember_email';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(email, password);
      const welcomeName = userData?.firstName || userData?.username || userData?.email?.split('@')[0] || 'Usuario';
      showSuccess(`¡Bienvenido, ${welcomeName}!`);
      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
      navigate(
        firstAllowedPath(
          userData.allowedModules,
          hasFullRbacMenuBypass(userData),
          userData.allowedActions
        )
      );
    } catch (err: any) {
      const code: string | undefined = err?.code;
      const details = (err?.details ?? {}) as {
        remainingAttempts?: number;
        minutesRemaining?: number;
        lockMinutes?: number;
      };

      if (code === 'ACCOUNT_JUST_LOCKED') {
        const minutes = details.lockMinutes ?? 15;
        showError(
          `Has superado el número de intentos permitidos. Tu cuenta está bloqueada por ${minutes} minutos.`
        );
      } else if (code === 'ACCOUNT_LOCKED') {
        const minutes = details.minutesRemaining ?? 0;
        showError(
          `Tu cuenta sigue bloqueada. Tiempo restante: ${minutes} minutos. Intenta más tarde o recupera tu contraseña.`
        );
      } else if (code === 'INVALID_CREDENTIALS' && typeof details.remainingAttempts === 'number') {
        showError(
          `Email o contraseña incorrectos. Te quedan ${details.remainingAttempts} intentos antes de que la cuenta sea bloqueada.`
        );
      } else {
        showError(err.message || 'Error al iniciar sesión. Verifica tus credenciales.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <XTAMSpinner variant="fullscreen" message="Iniciando sesión..." />;
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logos">
            <img
              src={logoXtam}
              alt="XTAM"
              className="login-logo-xtam"
            />
            <span className="login-vms-text">VMS</span>
          </div>
          <p className="login-subtitle">Video Management System</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@vms.local"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="form-options">
            <label className="remember-me">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <span>Recordarme</span>
            </label>
            <a href="#" className="recover-password-link" onClick={(e) => e.preventDefault()}>
              ¿Olvidaste tu contraseña?
            </a>
          </div>

          <button type="submit" className="login-button">
            Iniciar Sesión
          </button>
        </form>

        <div className="login-footer">
          <p>XTAM 2026 © Todos los derechos reservados</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
